import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiAlertTriangle, FiSearch, FiDownload, FiPrinter,
  FiPackage, FiAlertCircle, FiCheckCircle, FiRefreshCw,
  FiXCircle
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { getStockSummary } from '../../utils/stockFlowHelper';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const STATUS_COLORS = {
  outOfStock: '#DC3545',
  critical: '#FF6B35',
  warning: '#FFC107',
  ok: '#28A745'
};

const PIE_COLORS = [STATUS_COLORS.ok, STATUS_COLORS.warning, STATUS_COLORS.critical, STATUS_COLORS.outOfStock];

// ── Styled Components ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 170px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  padding-bottom: 2px;
`;

const ToggleLabel = styled.label`
  font-size: 13px;
  color: ${colors.text.primary};
  cursor: pointer;
  user-select: none;
`;

const ToggleSwitch = styled.div`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: ${props => props.$active ? colors.primary.main : colors.neutral[300]};
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$active ? '20px' : '2px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[200]}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-left: 4px solid ${props => props.$borderColor || colors.primary.main};
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const SummaryInfo = styled.div``;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;
  background: ${props => {
    if (props.$status === 'outOfStock') return `${STATUS_COLORS.outOfStock}08`;
    if (props.$status === 'critical') return `${STATUS_COLORS.critical}06`;
    if (props.$status === 'warning') return `${STATUS_COLORS.warning}06`;
    return 'transparent';
  }};

  &:hover {
    background: ${props => {
      if (props.$status === 'outOfStock') return `${STATUS_COLORS.outOfStock}12`;
      if (props.$status === 'critical') return `${STATUS_COLORS.critical}10`;
      if (props.$status === 'warning') return `${STATUS_COLORS.warning}10`;
      return colors.neutral[50];
    }};
  }
`;

const TrTotals = styled.tr`
  background: ${colors.neutral[50]};
  border-top: 2px solid ${colors.neutral[300]};
  font-weight: 600;
`;

const Td = styled.td`
  padding: 11px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$align || 'left'};
  white-space: nowrap;
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    if (props.$status === 'outOfStock') return STATUS_COLORS.outOfStock;
    if (props.$status === 'critical') return STATUS_COLORS.critical;
    if (props.$status === 'warning') return STATUS_COLORS.warning;
    return STATUS_COLORS.ok;
  }};
  flex-shrink: 0;
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusText = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${props => {
    if (props.$status === 'outOfStock') return STATUS_COLORS.outOfStock;
    if (props.$status === 'critical') return STATUS_COLORS.critical;
    if (props.$status === 'warning') return STATUS_COLORS.warning;
    return STATUS_COLORS.ok;
  }};
`;

const ItemCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 11px;
  padding: 3px 7px;
  background: ${colors.neutral[100]};
  border-radius: ${colors.borderRadius.sm};
  color: ${colors.text.secondary};
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${colors.primary.main}12;
  color: ${colors.primary.main};
`;

const ItemLink = styled.span`
  cursor: pointer;
  color: #1a73e8;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const ShortageCell = styled.span`
  color: ${props => props.$value > 0 ? STATUS_COLORS.outOfStock : colors.text.primary};
  font-weight: ${props => props.$value > 0 ? 600 : 400};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

const AutoRefreshWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const TableFooter = styled.div`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
  font-size: 13px;
  color: ${colors.text.secondary};
`;

const PieLegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const PieLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${colors.text.primary};
`;

const PieLegendDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const PieLegendValue = styled.span`
  margin-left: auto;
  font-weight: 600;
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
`;

// ── Helper: determine stock status ──
const getStockStatus = (currentQty, reorderLevel, minimumStock) => {
  const rl = reorderLevel || 0;
  const ms = minimumStock || (rl * 0.5);

  if (currentQty <= 0) return 'outOfStock';
  if (currentQty <= ms) return 'critical';
  if (currentQty <= rl) return 'warning';
  return 'ok';
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'outOfStock': return 'Out of Stock';
    case 'critical': return 'Critical';
    case 'warning': return 'Warning';
    default: return 'OK';
  }
};

const getStatusSortOrder = (status) => {
  switch (status) {
    case 'outOfStock': return 0;
    case 'critical': return 1;
    case 'warning': return 2;
    default: return 3;
  }
};

// ── Custom Tooltip for Bar Chart ──
const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: 'white',
      border: `1px solid ${colors.neutral[200]}`,
      borderRadius: 8,
      padding: '10px 14px',
      boxShadow: colors.shadows.md,
      fontSize: 13
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ color: entry.color, marginBottom: 2 }}>
          {entry.name}: {formatNumber(entry.value, { decimals: 2 })}
        </div>
      ))}
    </div>
  );
};

// ── Main Component ──
const LowStockAlert = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Data state
  const [allItems, setAllItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubCategory, setSelectedSubCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [alertLevel, setAlertLevel] = useState('all');
  const [showOnlyBelowReorder, setShowOnlyBelowReorder] = useState(true);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshIntervalRef = useRef(null);

  // ── Light master-data load for the filter dropdowns ──
  // (categories / sub-categories / locations only — the heavy inventoryItems +
  // stockFlow summary fetch is gated behind the Generate button)
  const loadFilterMasters = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [categoriesSnap, subCategoriesSnap, locationsSnap] = await Promise.all([
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryCategories`), orderBy('categoryCode'))),
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventorySubCategories`), orderBy('subCategoryCode'))),
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryLocations`), orderBy('locationCode')))
      ]);
      recordReads(activeCompany.id, categoriesSnap.size + subCategoriesSnap.size + locationsSnap.size);
      setCategories(categoriesSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true));
      setSubCategories(subCategoriesSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isDeleted !== true));
      setLocations(locationsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading filter masters:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFilterMasters();
  }, [loadFilterMasters]);

  // ── Load data ──
  const loadData = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);

      // Load categories
      const categoriesRef = collection(db, `companies/${activeCompany.id}/inventoryCategories`);
      const categoriesSnapshot = await getDocs(query(categoriesRef, orderBy('categoryCode')));
      const categoriesList = categoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true);
      setCategories(categoriesList);

      // Load subcategories
      const subCategoriesRef = collection(db, `companies/${activeCompany.id}/inventorySubCategories`);
      const subCategoriesSnapshot = await getDocs(query(subCategoriesRef, orderBy('subCategoryCode')));
      const subCategoriesList = subCategoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isDeleted !== true);
      setSubCategories(subCategoriesList);

      // Load locations
      const locationsRef = collection(db, `companies/${activeCompany.id}/inventoryLocations`);
      const locationsSnapshot = await getDocs(query(locationsRef, orderBy('locationCode')));
      const locationsList = locationsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setLocations(locationsList);

      // Load all inventoryItems
      const itemsRef = collection(db, `companies/${activeCompany.id}/inventoryItems`);
      const itemsSnapshot = await getDocs(query(itemsRef, orderBy('itemCode')));
      const inventoryItems = itemsSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true);

      recordReads(activeCompany.id, categoriesSnapshot.size + subCategoriesSnapshot.size + locationsSnapshot.size + itemsSnapshot.size);

      // Get stock summary from stockFlow
      const criteria = {};
      if (activeFinancialYear?.id) {
        criteria.financialYearId = activeFinancialYear.id;
      }
      if (activeBranch && activeBranch.id !== 'all') {
        criteria.branchId = activeBranch.id;
      }
      const stockData = await getStockSummary(activeCompany.id, criteria);

      // Build a lookup: itemId -> stock data
      const stockMap = {};
      stockData.forEach(entry => {
        // If there are multiple entries (e.g. by location), aggregate
        if (!stockMap[entry.itemId]) {
          stockMap[entry.itemId] = {
            currentQuantity: 0,
            currentValue: 0,
            averageCost: 0,
            quantityIn: 0,
            quantityOut: 0,
            totalCostIn: 0
          };
        }
        stockMap[entry.itemId].currentQuantity += entry.currentQuantity || 0;
        stockMap[entry.itemId].currentValue += entry.currentValue || 0;
        stockMap[entry.itemId].quantityIn += entry.quantityIn || 0;
        stockMap[entry.itemId].quantityOut += entry.quantityOut || 0;
        stockMap[entry.itemId].totalCostIn += entry.totalCostIn || 0;
        // INV-04: the summary already carries the item's weighted average.
        if (entry.averageCost > 0) stockMap[entry.itemId].weightedAverage = entry.averageCost;
      });

      // Average cost per item: the weighted average (old receipts average only
      // when the item has none yet)
      Object.keys(stockMap).forEach(itemId => {
        const s = stockMap[itemId];
        s.averageCost = s.weightedAverage || (s.quantityIn > 0 ? s.totalCostIn / s.quantityIn : 0);
      });

      // Build enriched item list
      const enrichedItems = inventoryItems.map(item => {
        const stock = stockMap[item.id] || { currentQuantity: 0, currentValue: 0, averageCost: 0 };
        const currentQty = stock.currentQuantity;
        const reorderLevel = item.reorderQuantity || 0;
        const minimumStock = reorderLevel * 0.5;
        const reorderQuantity = item.reorderQuantity || 0;
        const status = getStockStatus(currentQty, reorderLevel, minimumStock);
        const shortage = Math.max(0, reorderLevel - currentQty);
        const estCost = reorderQuantity > 0
          ? reorderQuantity * (stock.averageCost || item.purchasePrice || 0)
          : shortage * (stock.averageCost || item.purchasePrice || 0);

        const category = categoriesList.find(c => c.id === item.categoryId);
        const subCategory = subCategoriesList.find(s => s.id === item.subCategoryId);

        return {
          id: item.id,
          itemCode: item.itemCode || '',
          itemName: item.itemName || '',
          categoryId: item.categoryId || '',
          subCategoryId: item.subCategoryId || '',
          categoryName: category?.categoryName || 'Uncategorized',
          subCategoryName: subCategory?.subCategoryName || '',
          unitOfMeasure: item.unitOfMeasure || 'Pcs',
          salePrice: item.salePrice || 0,
          reorderLevel,
          reorderQuantity,
          minimumStock,
          currentQuantity: currentQty,
          averageCost: stock.averageCost,
          currentValue: stock.currentValue,
          status,
          shortage,
          estCost
        };
      });

      // Sort: most critical first, then by item code
      enrichedItems.sort((a, b) => {
        const orderDiff = getStatusSortOrder(a.status) - getStatusSortOrder(b.status);
        if (orderDiff !== 0) return orderDiff;
        return (a.itemCode || '').localeCompare(b.itemCode || '');
      });

      setAllItems(enrichedItems);
    } catch (error) {
      console.error('Error loading low stock data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeFinancialYear, activeBranch]);

  // Heavy inventoryItems + stockFlow fetch is user-triggered only (Generate button)
  // — no auto-load on mount. All filters here are client-side, so they keep working
  // instantly on the already-loaded data after the first Generate.
  const handleGenerate = useCallback(() => {
    setHasGenerated(true);
    loadData();
  }, [loadData]);

  // Auto-refresh logic — only active once the user has generated the report
  useEffect(() => {
    if (autoRefresh && hasGenerated) {
      refreshIntervalRef.current = setInterval(() => {
        loadData();
      }, 60000); // Refresh every 60 seconds
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, hasGenerated, loadData]);

  // Reset subcategory when category changes
  useEffect(() => {
    setSelectedSubCategory('all');
  }, [selectedCategory]);

  // ── Filtered subcategories ──
  const filteredSubCategories = selectedCategory === 'all'
    ? subCategories
    : subCategories.filter(sc => sc.categoryId === selectedCategory);

  // ── Filter items ──
  const filteredItems = allItems.filter(item => {
    // Search
    const matchesSearch = !searchQuery ||
      item.itemName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.itemCode?.toLowerCase().includes(searchQuery.toLowerCase());

    // Category
    const matchesCategory = selectedCategory === 'all' || item.categoryId === selectedCategory;

    // SubCategory
    const matchesSubCategory = selectedSubCategory === 'all' || item.subCategoryId === selectedSubCategory;

    // Alert Level
    let matchesAlert = true;
    if (alertLevel === 'critical') matchesAlert = item.status === 'critical' || item.status === 'outOfStock';
    else if (alertLevel === 'warning') matchesAlert = item.status === 'warning';
    else if (alertLevel === 'ok') matchesAlert = item.status === 'ok';
    else if (alertLevel === 'outOfStock') matchesAlert = item.status === 'outOfStock';

    // Show Only Below Reorder toggle
    const matchesBelowReorder = !showOnlyBelowReorder || item.status !== 'ok';

    return matchesSearch && matchesCategory && matchesSubCategory && matchesAlert && matchesBelowReorder;
  });

  // ── Summary calculations ──
  const totalItems = allItems.length;
  const itemsBelowReorder = allItems.filter(i => i.status === 'warning' || i.status === 'critical' || i.status === 'outOfStock').length;
  const itemsOutOfStock = allItems.filter(i => i.status === 'outOfStock').length;
  const itemsWarning = allItems.filter(i => i.status === 'warning' || i.status === 'critical').length;

  // ── Chart Data ──
  // Bar chart: top 15 items below reorder level
  const barChartItems = allItems
    .filter(i => i.status !== 'ok')
    .slice(0, 15);

  const barChartData = barChartItems.map(item => ({
    name: item.itemCode || item.itemName?.substring(0, 12) || '-',
    'Current Qty': Math.max(0, item.currentQuantity),
    'Reorder Level': item.reorderLevel
  }));

  // Pie chart: status distribution
  const okCount = allItems.filter(i => i.status === 'ok').length;
  const warningCount = allItems.filter(i => i.status === 'warning').length;
  const criticalCount = allItems.filter(i => i.status === 'critical').length;
  const outOfStockCount = allItems.filter(i => i.status === 'outOfStock').length;

  const pieChartData = [
    { name: 'OK', value: okCount, color: STATUS_COLORS.ok },
    { name: 'Warning', value: warningCount, color: STATUS_COLORS.warning },
    { name: 'Critical', value: criticalCount, color: STATUS_COLORS.critical },
    { name: 'Out of Stock', value: outOfStockCount, color: STATUS_COLORS.outOfStock }
  ].filter(d => d.value > 0);

  // ── Totals for filtered table ──
  const totalShortage = filteredItems.reduce((sum, i) => sum + i.shortage, 0);
  const totalEstCost = filteredItems.reduce((sum, i) => sum + i.estCost, 0);
  const totalCurrentQty = filteredItems.reduce((sum, i) => sum + i.currentQuantity, 0);

  // ── CSV Export ──
  const handleExport = () => {
    let csv = 'Low Stock Alert Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}\n\n`;
    csv += 'Status,Item Code,Item Name,Category,Unit,Current Qty,Reorder Level,Min Stock,Shortage,Reorder Qty,Est. Cost\n';

    filteredItems.forEach(item => {
      csv += `${getStatusLabel(item.status)},`;
      csv += `${item.itemCode || ''},`;
      csv += `"${(item.itemName || '').replace(/"/g, '""')}",`;
      csv += `"${(item.categoryName || '').replace(/"/g, '""')}",`;
      csv += `${item.unitOfMeasure || ''},`;
      csv += `${item.currentQuantity || 0},`;
      csv += `${item.reorderLevel || 0},`;
      csv += `${item.minimumStock || 0},`;
      csv += `${item.shortage || 0},`;
      csv += `${item.reorderQuantity || 0},`;
      csv += `${item.estCost || 0}\n`;
    });

    csv += `\nTotal Items in Report:,${filteredItems.length}\n`;
    csv += `Items Below Reorder:,${itemsBelowReorder}\n`;
    csv += `Items Out of Stock:,${itemsOutOfStock}\n`;
    csv += `Total Shortage:,${totalShortage}\n`;
    csv += `Total Estimated Cost:,${totalEstCost}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Low_Stock_Alert_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    if (filteredItems.length === 0) { alert('No data to print'); return; }

    const statusColor = (s) => {
      switch (s) {
        case 'outOfStock': return '#DC3545';
        case 'critical': return '#FF6B35';
        case 'warning': return '#FFC107';
        default: return '#28A745';
      }
    };

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Low Stock Alert</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:18px}
        .summary{display:flex;gap:24px;margin-bottom:18px;padding:12px 15px;background:#f9f9f9;border-radius:8px;flex-wrap:wrap}
        .summary-item{text-align:center;min-width:100px}
        .summary-label{font-size:10px;color:#666;margin-bottom:3px}
        .summary-value{font-size:15px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        th.center{text-align:center}
        td{padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .status-badge{font-size:9px;padding:2px 6px;border-radius:3px;color:white;font-weight:600;display:inline-block}
        .totals-row{background:#f5f5f5;font-weight:700;border-top:2px solid #800020}
        @media print{body{margin:15px}@page{size:landscape}}
      </style></head><body>
      <h1>Low Stock Alert</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Report Date: ${new Date().toLocaleDateString()} |
        Showing ${filteredItems.length} of ${totalItems} items
      </div>
      <div class="summary">
        <div class="summary-item"><div class="summary-label">Total Items</div><div class="summary-value">${totalItems}</div></div>
        <div class="summary-item"><div class="summary-label">Items Below Reorder</div><div class="summary-value" style="color:#FFC107">${itemsBelowReorder}</div></div>
        <div class="summary-item"><div class="summary-label">Out of Stock</div><div class="summary-value" style="color:#DC3545">${itemsOutOfStock}</div></div>
        <div class="summary-item"><div class="summary-label">Total Shortage</div><div class="summary-value" style="color:#DC3545">${formatNumber(totalShortage, { decimals: 2 })}</div></div>
        <div class="summary-item"><div class="summary-label">Est. Reorder Cost</div><div class="summary-value">${formatCurrency(totalEstCost, { decimals: 2 })}</div></div>
      </div>
      <table>
        <thead><tr>
          <th class="center">Status</th><th>Item Code</th><th>Item Name</th><th>Category</th><th class="center">Unit</th>
          <th class="right">Current Qty</th><th class="right">Reorder Level</th><th class="right">Min Stock</th>
          <th class="right">Shortage</th><th class="right">Reorder Qty</th><th class="right">Est. Cost</th>
        </tr></thead>
        <tbody>
          ${filteredItems.map(item => `<tr>
            <td style="text-align:center"><span class="status-badge" style="background:${statusColor(item.status)}">${getStatusLabel(item.status)}</span></td>
            <td>${item.itemCode || '-'}</td>
            <td>${item.itemName}</td>
            <td>${item.categoryName}</td>
            <td style="text-align:center">${item.unitOfMeasure}</td>
            <td class="amount" style="color:${item.currentQuantity <= 0 ? '#DC3545' : item.status === 'critical' ? '#FF6B35' : '#1A1A1A'};font-weight:${item.status !== 'ok' ? 600 : 400}">${formatNumber(item.currentQuantity, { decimals: 2 })}</td>
            <td class="amount">${formatNumber(item.reorderLevel, { decimals: 2 })}</td>
            <td class="amount">${formatNumber(item.minimumStock, { decimals: 2 })}</td>
            <td class="amount" style="color:${item.shortage > 0 ? '#DC3545' : '#1A1A1A'}">${item.shortage > 0 ? formatNumber(item.shortage, { decimals: 2 }) : '-'}</td>
            <td class="amount">${item.reorderQuantity > 0 ? formatNumber(item.reorderQuantity, { decimals: 2 }) : '-'}</td>
            <td class="amount">${item.estCost > 0 ? formatCurrency(item.estCost, { decimals: 2 }) : '-'}</td>
          </tr>`).join('')}
          <tr class="totals-row">
            <td colspan="5" style="text-align:center;font-size:12px">TOTALS (${filteredItems.length} items)</td>
            <td class="amount">${formatNumber(totalCurrentQty, { decimals: 2 })}</td>
            <td class="amount">-</td>
            <td class="amount">-</td>
            <td class="amount" style="color:#DC3545">${formatNumber(totalShortage, { decimals: 2 })}</td>
            <td class="amount">-</td>
            <td class="amount">${formatCurrency(totalEstCost, { decimals: 2 })}</td>
          </tr>
        </tbody>
      </table>
      <div style="font-size:10px;color:#999;margin-top:10px">Printed on ${new Date().toLocaleString()}</div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Low Stock Alert" breadcrumb="Reports / Inventory Reports / Low Stock Alert">
      {/* ── Summary Cards ── */}
      {hasGenerated && !loading && (
      <SummaryCards>
        <SummaryCard $borderColor={colors.primary.main}>
          <SummaryIcon $color={colors.primary.main}>
            <FiPackage />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Items</SummaryLabel>
            <SummaryValue>{totalItems}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>

        <SummaryCard $borderColor={STATUS_COLORS.warning}>
          <SummaryIcon $color={STATUS_COLORS.warning}>
            <FiAlertTriangle />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Items Below Reorder</SummaryLabel>
            <SummaryValue>{itemsBelowReorder}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>

        <SummaryCard $borderColor={STATUS_COLORS.outOfStock}>
          <SummaryIcon $color={STATUS_COLORS.outOfStock}>
            <FiXCircle />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Items Out of Stock</SummaryLabel>
            <SummaryValue>{itemsOutOfStock}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>

        <SummaryCard $borderColor={STATUS_COLORS.critical}>
          <SummaryIcon $color={STATUS_COLORS.critical}>
            <FiAlertCircle />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Warning / Critical</SummaryLabel>
            <SummaryValue>{itemsWarning}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
      </SummaryCards>
      )}

      {/* ── Filters ── */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Category</Label>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.categoryName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Sub Category</Label>
            <Select
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
            >
              <option value="all">All Sub Categories</option>
              {filteredSubCategories.map(sc => (
                <option key={sc.id} value={sc.id}>{sc.subCategoryName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Location</Label>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Alert Level</Label>
            <Select
              value={alertLevel}
              onChange={(e) => setAlertLevel(e.target.value)}
            >
              <option value="all">All Levels</option>
              <option value="outOfStock">Out of Stock</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="ok">OK</option>
            </Select>
          </FilterGroup>

          <SearchInput>
            <SearchIconWrap><FiSearch /></SearchIconWrap>
            <Input
              type="text"
              placeholder="Search by item code or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInput>
        </FiltersRow>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginTop: 14 }}>
          <ToggleRow>
            <ToggleSwitch
              $active={showOnlyBelowReorder}
              onClick={() => setShowOnlyBelowReorder(prev => !prev)}
            />
            <ToggleLabel onClick={() => setShowOnlyBelowReorder(prev => !prev)}>
              Show Only Below Reorder
            </ToggleLabel>
          </ToggleRow>

          <AutoRefreshWrap>
            <ToggleSwitch
              $active={autoRefresh}
              onClick={() => setAutoRefresh(prev => !prev)}
            />
            <span style={{ cursor: 'pointer' }} onClick={() => setAutoRefresh(prev => !prev)}>
              Auto-Refresh (60s)
            </span>
            <FiRefreshCw
              size={14}
              style={{ cursor: 'pointer', color: colors.primary.main }}
              onClick={handleGenerate}
              title="Refresh Now"
            />
          </AutoRefreshWrap>

          <ButtonGroup>
            <Button $primary onClick={handleGenerate} disabled={loading}>
              <FiAlertTriangle /> {loading ? 'Loading...' : 'Generate'}
            </Button>
            <Button onClick={handleExport} disabled={filteredItems.length === 0}>
              <FiDownload /> CSV Export
            </Button>
            <Button onClick={handlePrint} disabled={filteredItems.length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button
              disabled={filteredItems.length === 0}
              onClick={() => {
                if (filteredItems.length === 0) { alert('No items to add to a Purchase Order.'); return; }
                const prefillLines = filteredItems.map(item => ({
                  itemId: item.itemId || item.id,
                  itemCode: item.itemCode || '',
                  itemName: item.itemName || '',
                  description: item.itemName || '',
                  quantity: Math.max(item.shortage || 0, item.reorderQuantity || 0, 1),
                  unitOfMeasure: item.unitOfMeasure || 'Pcs',
                  unitPrice: item.averageCost || item.purchasePrice || 0,
                  unitCost: item.averageCost || item.purchasePrice || 0
                }));
                navigate('/purchases/purchase-order', {
                  state: { prefill: { lines: prefillLines, source: 'LowStockAlert' } }
                });
              }}
              style={{ background: colors.warning.main, color: 'white' }}
            >
              <FiPackage /> Create PO ({filteredItems.length})
            </Button>
          </ButtonGroup>
        </div>
      </FiltersCard>

      {/* ── Data Table ── */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading inventory data...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon><FiAlertTriangle /></EmptyIcon>
              <EmptyText>Select your filters and click Generate to view low stock alerts</EmptyText>
            </EmptyState>
          ) : filteredItems.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                {allItems.filter(i => i.status !== 'ok').length === 0
                  ? <FiCheckCircle style={{ color: STATUS_COLORS.ok }} />
                  : <FiPackage />
                }
              </EmptyIcon>
              <EmptyText>
                {allItems.filter(i => i.status !== 'ok').length === 0
                  ? 'All items are adequately stocked!'
                  : 'No items match your current filter criteria.'
                }
              </EmptyText>
              {allItems.filter(i => i.status !== 'ok').length === 0 && (
                <p style={{ fontSize: 14, color: colors.text.tertiary }}>
                  No items are currently below their reorder levels.
                </p>
              )}
            </EmptyState>
          ) : (
            <>
              <Table>
                <Thead>
                  <tr>
                    <Th $align="center">Status</Th>
                    <Th>Item Code</Th>
                    <Th>Item Name</Th>
                    <Th>Category</Th>
                    <Th $align="center">Unit</Th>
                    <Th $align="right">Current Qty</Th>
                    <Th $align="right">Reorder Level</Th>
                    <Th $align="right">Min Stock</Th>
                    <Th $align="right">Shortage</Th>
                    <Th $align="right">Reorder Qty</Th>
                    <Th $align="right">Est. Cost</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredItems.map(item => (
                    <Tr key={item.id} $status={item.status}>
                      <Td $align="center">
                        <StatusCell style={{ justifyContent: 'center' }}>
                          <StatusDot $status={item.status} />
                          <StatusText $status={item.status}>
                            {getStatusLabel(item.status)}
                          </StatusText>
                        </StatusCell>
                      </Td>
                      <Td><ItemCode>{item.itemCode || '-'}</ItemCode></Td>
                      <Td>
                        <ItemLink onClick={() => navigate('/reports/inventory-ledger', { state: { itemId: item.itemId || item.id, itemName: item.itemName } })}>
                          {item.itemName}
                        </ItemLink>
                      </Td>
                      <Td><CategoryBadge>{item.categoryName}</CategoryBadge></Td>
                      <Td $align="center">{item.unitOfMeasure}</Td>
                      <AmountCell $align="right">
                        <span style={{
                          color: item.currentQuantity <= 0
                            ? STATUS_COLORS.outOfStock
                            : item.status === 'critical'
                              ? STATUS_COLORS.critical
                              : colors.text.primary,
                          fontWeight: item.status !== 'ok' ? 600 : 400
                        }}>
                          {formatNumber(item.currentQuantity, { decimals: 2 })}
                        </span>
                      </AmountCell>
                      <AmountCell $align="right">{formatNumber(item.reorderLevel, { decimals: 2 })}</AmountCell>
                      <AmountCell $align="right">{formatNumber(item.minimumStock, { decimals: 2 })}</AmountCell>
                      <AmountCell $align="right">
                        <ShortageCell $value={item.shortage}>
                          {item.shortage > 0 ? formatNumber(item.shortage, { decimals: 2 }) : '-'}
                        </ShortageCell>
                      </AmountCell>
                      <AmountCell $align="right">
                        {item.reorderQuantity > 0
                          ? formatNumber(item.reorderQuantity, { decimals: 2 })
                          : '-'
                        }
                      </AmountCell>
                      <AmountCell $align="right">
                        {item.estCost > 0
                          ? formatCurrency(item.estCost, { decimals: 2 })
                          : '-'
                        }
                      </AmountCell>
                    </Tr>
                  ))}

                  {/* Totals Row */}
                  <TrTotals>
                    <Td $align="center" colSpan={5} style={{ fontWeight: 600, fontSize: 13 }}>
                      TOTALS ({filteredItems.length} items)
                    </Td>
                    <AmountCell $align="right" style={{ fontWeight: 700 }}>
                      {formatNumber(totalCurrentQty, { decimals: 2 })}
                    </AmountCell>
                    <AmountCell $align="right">-</AmountCell>
                    <AmountCell $align="right">-</AmountCell>
                    <AmountCell $align="right" style={{ fontWeight: 700, color: STATUS_COLORS.outOfStock }}>
                      {formatNumber(totalShortage, { decimals: 2 })}
                    </AmountCell>
                    <AmountCell $align="right">-</AmountCell>
                    <AmountCell $align="right" style={{ fontWeight: 700, color: colors.primary.main }}>
                      {formatCurrency(totalEstCost, { decimals: 2 })}
                    </AmountCell>
                  </TrTotals>
                </Tbody>
              </Table>

              <TableFooter>
                <span>
                  Showing {filteredItems.length} of {allItems.length} items
                  {showOnlyBelowReorder ? ' (below reorder only)' : ''}
                </span>
                <span>
                  Out of Stock: {outOfStockCount} | Critical: {criticalCount} | Warning: {warningCount} | OK: {okCount}
                </span>
              </TableFooter>
            </>
          )}
        </TableContainer>
      </ContentCard>

      {/* ── Charts ── */}
      {!loading && allItems.length > 0 && (
        <ChartsRow>
          <ChartCard>
            <ChartTitle>Items Below Reorder Level (Current Qty vs Reorder Level)</ChartTitle>
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={barChartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: colors.text.secondary }}
                    angle={-35}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fontSize: 11, fill: colors.text.secondary }} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Current Qty" fill={STATUS_COLORS.outOfStock} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Reorder Level" fill={colors.primary.main} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: colors.text.secondary }}>
                <FiCheckCircle size={32} style={{ marginBottom: 8, color: STATUS_COLORS.ok }} /><br />
                All items are above reorder level.
              </div>
            )}
          </ChartCard>

          <ChartCard>
            <ChartTitle>Stock Status Distribution</ChartTitle>
            {pieChartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} items`, name]}
                      contentStyle={{
                        borderRadius: 8,
                        border: `1px solid ${colors.neutral[200]}`,
                        fontSize: 13
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <PieLegendList>
                  {pieChartData.map((entry, index) => (
                    <PieLegendItem key={index}>
                      <PieLegendDot $color={entry.color} />
                      <span>{entry.name}</span>
                      <PieLegendValue>{entry.value}</PieLegendValue>
                    </PieLegendItem>
                  ))}
                </PieLegendList>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: colors.text.secondary }}>
                No data available.
              </div>
            )}
          </ChartCard>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

export default LowStockAlert;
